import React, { Component } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import styled from 'styled-components'
import Select from 'react-select'
import moment from "moment-timezone";
import request from "../../utils/request";
import patient from "../../stores/patient";
import {observer} from "mobx-react";

const EventContainer = styled.div`
  background-color: ${({ color }) => color};
  color: ${({ textColor }) => textColor}; /* Dynamically set text color */
  padding: 5px;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  white-space: normal;
  word-wrap: break-word;
  width: 100%;
  height: 100%;

  @media (max-width: 600px) {
    padding: 3px;
    font-size: 10px;
  }
`;

const EventTime = styled.div`
  font-size: 14px;
  font-weight: bold; /* Make text more prominent */
  color: inherit; /* Use inherited text color */
  margin-bottom: 5px;

  @media (max-width: 600px) {
    font-size: 12px; /* Adjust for smaller screens */
  }
`;

const EventTitle = styled.div`
  font-size: 12px;
  color: inherit; /* Use inherited text color */
  @media (max-width: 600px) {
    font-size: 10px;
  }
`;

const Eye = styled.span.attrs({ className: "fas fa-eye" })`
    color: ${props => props.active ? '#000' : 'var(--light-gray)'};
    margin-left: 0.25rem;
`

const SelectWrapper = styled.div`
  margin-top: 8px;
  margin-bottom: 16px; /* Default margin */
  
  @media (max-width: 768px) {
    margin-top: 6px;
    margin-bottom: 12px; /* Adjust for tablets */
  }
  
  @media (max-width: 480px) {
    margin-top: 4px;
    margin-bottom: 8px; /* Adjust for mobile devices */
  }
`

const Legend = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  margin-bottom: 16px;
  font-size: 14px;

  @media (max-width: 600px) {
    font-size: 12px;
  }
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  margin-right: 16px;

  @media (max-width: 600px) {
    margin-right: 8px;
  }
`;

const ColorBox = styled.div`
  width: 16px;
  height: 16px;
  background-color: ${({ color }) => color};
  border-radius: 4px;
  margin-right: 8px;

  @media (max-width: 600px) {
    width: 12px;
    height: 12px;
  }
`;

@observer
export class CalendarStats extends Component {

  patientID = patient.fields._id;

  eventContent = (info) => {
    const { finalAdherence, eye } = info.event.extendedProps;
    const color = finalAdherence === 1 ? 'green'
      : finalAdherence === 0? 'yellow': 'red'; // Background color
    const textColor = finalAdherence === 0 ? 'black' : 'white'; // Text color based on background
    const time = finalAdherence === -1? 'Missing': info.timeText;
    const tooltipContent = finalAdherence === 0 ? "Taken Outside the Window" : "";

    return (
      <EventContainer color={color} textColor={textColor}>
        <EventTime>{time} - {eye}</EventTime>
        <EventTitle>{info.event.title}</EventTitle>
      </EventContainer>
    );
  };

  constructor(props) {
    super(props)
    this.state = {
      selectedMedicines: [],
      currentMonth: 0,
      events: [],
      medicineOptions: [],
    }
  }

  // Fetch events from the backend
  fetchEvents = async (patientID) => {
    const res = await request('/api/adherenceStats/getDailyReports', {
      patientID
    });
    if (res.success) {
      const events = res.data.dailyReports.map((report) => {
        const startStr = report.medicationTime.replace(' ', 'T');
        const start = moment.tz(startStr, 'America/New_York');
        const end = start.clone().add(30, 'minutes');

        if(report.finalAdherence !== -1) {
          return {
            title: report.prescription,
            start: startStr,
            end: end.format('YYYY-MM-DDTHH:mm:ss'),
            finalAdherence: report.finalAdherence,
            eye: report.eye,
            allDay: false,
          }
        } else {
          return {
            title: report.prescription,
            start: startStr,
            end: end.format('YYYY-MM-DDTHH:mm:ss'),
            finalAdherence: report.finalAdherence,
            eye: report.eye,
            allDay: true,
          }
        }
      })
      this.setState({ events });
      this.setMedicineOptions(events);
    } else {
      console.error('Failed to fetch events');
    }
  }

  setMedicineOptions = (events) => {
    const uniqueTitles = new Set();
    events.forEach(event => {
      uniqueTitles.add(event.title);
    });
    const medicineOptions = Array.from(uniqueTitles).map(title => ({
      value: title,
      label: title
    }));
    this.setState({ medicineOptions });
  }

  componentDidMount() {
    this.fetchEvents(this.patientID).then();
  }

  handleFilterChange = (selectedOptions) => this.setState({...this.state, selectedMedicines: selectedOptions})

  getFilteredEvents() {
    const { selectedMedicines, events } = this.state;
    if (selectedMedicines.length === 0) {
      return events;
    }
    return events.filter(event => selectedMedicines.some(med => med.value === event.title));
  }
  handleDatesSet = (dateInfo) => {
    this.setState({...this.state, currentMonth: dateInfo.start.getMonth()})
  }
  render() {
    const { selectedMedicines } = this.state;
    const filteredEvents = this.getFilteredEvents();

    return (
      <div>
        <label htmlFor="filter">Filter by Medicine: </label>
        <SelectWrapper>
          <Select
            id="filter"
            isMulti
            options={this.state.medicineOptions}
            value={selectedMedicines}
            onChange={this.handleFilterChange}
            className="multi-select"
            classNamePrefix="select"
          />
        </SelectWrapper>
        <Legend>
          <LegendItem>
            <ColorBox color="green" />
            <span>Taken on Time</span>
          </LegendItem>
          <LegendItem>
            <ColorBox color="yellow" />
            <span>Taken Outside the Window</span>
          </LegendItem>
          <LegendItem>
            <ColorBox color="red" />
            <span>Missing</span>
          </LegendItem>
        </Legend>
        <FullCalendar plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                      initialView="dayGridMonth" firstDay={1}
                      events={filteredEvents}
                      eventContent={this.eventContent}
                      datesSet={this.handleDatesSet}
                      timeZone='America/New_York'
        />
      </div>
    )

  }
}


// WEBPACK FOOTER //
// ./src/pages/AdherenceStats/CalendarStats.jsx